import { DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { CommonFunctions } from '@shared/common-functions';
import {
  END_DATE_GREATER,
  getMonthAbbreviation,
  USER_ROLES,
} from '@shared/constant/shared.constant';
import { DigitFormatPipe } from '@shared/pipes/digits-format.pipe';
import {
  BreadcrumbItem,
  CompanyService,
} from '@shared/services/company.service';
import { ToastService } from '@shared/services/toast.service';
import { autoUnsubWith, Utility } from '@shared/services/utility.abstract';
import { ChartData, ChartOptions } from 'chart.js';
import {
  ParkingLot,
  ResponseApiResponse,
  ReqManagerId,
} from '@core/models/api-response.model';
import { SuperAdminService } from '@core/services/super-admin.service';
import { MetricDisplayComponent } from '../metric-display/metric-display.component';
import { EncryptionStorageService } from '@core/services/encryption-storage.service';

@Component({
  selector: 'app-common-dashboard',
  templateUrl: './common-dashboard.component.html',
  styleUrls: ['./common-dashboard.component.scss'],
})
export class CommonDashboardComponent implements OnInit {
  @ViewChild(MetricDisplayComponent) metricDisplayComponent!: MetricDisplayComponent;
  readonly destroyRef = inject(DestroyRef);
  end: any = '';
  start: any = '';
  startdate: Date;
  enddate: Date;
  startTime: string = '';
  endTime: string = '';
  totalBookings: number = 0;
  totalRevenue: number = 0;
  peakHoursStartTime: string;
  same: boolean = true;
  peakHoursEndTime: string;
  occupiedCount: number;
  vacantCount: number;
  maintenanceCount: number;
  groupBookingCount: number;
  groupBookingSlotCounts: number;
  oneTimeBookingCount: number;
  parkerCount: number;
  parkingLotByStates: ParkingLot[];
  selectedParkingLot: string = null;
  revenueData: ChartData;
  chartOptions: ChartOptions;
  chartOptionsForGraph: ChartOptions;
  parkingLotsData: ChartData;
  parkingStatsData: any;
  parkingStatsOptions: any;
  id: string;
  year: any[] = [];
  checkStartDate: any;
  checkEndDate: any;
  selectedYear;
  endDateBoard: Date;
  startDateBoard: Date;
  firstDayOfMonth: Date;
  role: string;
  dateValidationError:boolean = false;
  searchclick: any;
  dateRangeData: any;
  revenueWithTransactionFees: number;
  revenueWithTaxAndFees: number;
  userRole = USER_ROLES;
  onCompanyChange: boolean;

  constructor(
    private readonly superAdminService: SuperAdminService,
    private readonly toastService: ToastService,
    private readonly datePipe: DatePipe,
    private readonly route: ActivatedRoute,
    private readonly companyService: CompanyService,
    private readonly encryptStorage: EncryptionStorageService
  ) { }

  ngOnInit() {
    window.scroll(0, 0);
    this.role = this.encryptStorage.getItem('roleName');
    this.start = new Date();
    this.end = new Date();
    this.firstDayOfMonth = new Date();
    this.firstDayOfMonth.setDate(1);
    this.startDateBoard = this.firstDayOfMonth;
    this.endDateBoard = new Date();
    this.generateYears();
    this.same = false;
    this.checkEndDate = CommonFunctions.formatDatePlaceholder(
      this.end,
      this.datePipe
    );

    this.checkStartDate = CommonFunctions.formatDatePlaceholder(
      this.firstDayOfMonth.toDateString(),
      this.datePipe
    );
    this.start = CommonFunctions.formatDatePlaceholder(
      this.firstDayOfMonth.toDateString(),
      this.datePipe
    );
    this.end = CommonFunctions.formatDatePlaceholder(this.end, this.datePipe);
    this.selectedYear = this.year[this.year.length - 1].name;
    if (this.role === this.userRole.SUPER_ADMIN) {
      this.route.params.subscribe((params: Params) => {
        this.id = params['id'];
        this.onCompanyChange = history?.state?.onCompanyChange;
        const breadcrumbObj: BreadcrumbItem[] = [
          {
            title: 'Parking Statistics',
          },
        ];

        this.companyService.setAdminHeaders(this.id, breadcrumbObj);
        this.metricDisplayComponent?.resetfilter(true);
        const newState = { ...history.state };
        newState.onCompanyChange = false;
        history.replaceState(newState, '');
        this.loadData(this.id);
      });
    } else {
      this.loadData(null);
    }
  }

  private loadData(companyId: string | null): void {
    const validCompanyId = companyId && companyId > '0' ? companyId : null;
    this.getParkingLotByStatesName(validCompanyId);
    if(!this.onCompanyChange){
    this.searchByParkingLot();
    }
  }

  getParkingLotByStatesName(id: string) {
    this.superAdminService.getParkingLotByStatesName(id)
    .pipe(autoUnsubWith(this.destroyRef))
    .subscribe({
      next: (response: ParkingLot[]) => {
        this.updateParkingLotState(response);
      },
      error: () => {
        this.updateParkingLotState([]);
      },
    });
  }

  private updateParkingLotState(parkingLots: ParkingLot[]) {
    parkingLots.unshift({ id: null, name: `All Parking Lots (${parkingLots.length})` });
    this.selectedParkingLot = parkingLots[0].id;
    this.parkingLotByStates = parkingLots;
  }

  search(event,onSerchClick: string = '') {
    if(onSerchClick){
      this.searchclick = !this.searchclick
    }
    this.start = event.startDateBoard;
    this.end = event.endDateBoard;

    const startDate = new Date(this.start);
    const endDate = new Date(this.end);

    this.checkStartDate = CommonFunctions.formatDatePlaceholder(
      this.start,
      this.datePipe
    );
    this.checkEndDate = CommonFunctions.formatDatePlaceholder(
      this.end,
      this.datePipe
    );

    const startDateTime = startDate.getTime();
    const endDateTime = endDate.getTime();

    this.same = startDateTime === endDateTime;

    if (startDateTime > endDateTime) {
      this.dateValidationError = true;
      this.toastService.showToastMessage('error', 'Error', END_DATE_GREATER);
      return;
    } else {
      this.dateValidationError = false;
      this.searchByParkingLot();
    }

  }

  searchByParkingLot() {
    if (this.start && this.end) {
      this.selectedParkingLot =
        this.selectedParkingLot === '1' ? null : this.selectedParkingLot;
      const req: ReqManagerId = {
        startDate: CommonFunctions.formatDate(this.start, this.datePipe),
        endDate: CommonFunctions.formatDate(this.end,this.datePipe),
        location: this.selectedParkingLot || '',
        timeZone: Utility.getLocalTimeZone(),
        companyId: this.id,
      };

      this.getDashboardDataByManagerId(req);
      this.getOccupiedAndVacantParkingLots(req);
      this.loadGraphData(this.id, this.selectedYear, this.selectedParkingLot);
    }
  }

async loadGraphData(
  companyId: string,
  selectedYear: number,
  parkingLotId: string
) {
  const parkingLotsDataPromise = this.getParkingLotsData(
    companyId,
    selectedYear,
    parkingLotId
  ).toPromise();

  const graphRevenueDetailsPromise = this.getGraphRevenueDetails(
    companyId,
    selectedYear,
    parkingLotId
  ).toPromise();

  const [parkingLotsResponse, revenueResponse] = await Promise.all([
    parkingLotsDataPromise,
    graphRevenueDetailsPromise,
  ]);

  this.createOccupancyChart(parkingLotsResponse as any[]);
  this.createRevenueChart(revenueResponse as any[]);
}


  getGraphRevenueDetails(companyId, year, parkingLotId) {
    const req = {
      companyId: companyId,
      year: year,
      parkingLotId: parkingLotId,
      startDate: CommonFunctions.formatDate(this.start, this.datePipe),
      endDate: CommonFunctions.formatDate(this.end,this.datePipe),
      timeZone: Utility.getLocalTimeZone(),
    };
    return this.superAdminService.getGraphRevenueDetailsSuperAdmin(req);
  }

  getParkingLotsData(
    companyId: string,
    selectedYear: number,
    parkingLotId: string
  ) {
    const req = {
      companyId: companyId,
      year: selectedYear,
      timeZone: Utility.getLocalTimeZone(),
      parkingLotId: parkingLotId,
      startDate: CommonFunctions.formatDate(this.start,this.datePipe),
      endDate: CommonFunctions.formatDate(this.end,this.datePipe),
    };
    return this.superAdminService.getOccupiedAndVacantParkingForLotsGraph(req);
  }

  createOccupancyChart(data: any[]) {
    const labels = data.map((item) => getMonthAbbreviation(item.month));
    const occupancyRate = [];
    const vacant = [];
    data.forEach((item) => {
      occupancyRate.push(
        item.parkingLots.occupancyRate > 0 ? item.parkingLots.occupancyRate : 0
      );
      vacant.push(item.parkingLots.vacant > 0 ? item.parkingLots.vacant : 0);
    });

    this.parkingLotsData = {
      labels: labels,
      datasets: [
        {
          label: 'Occupied',
          data: occupancyRate,
          backgroundColor: 'rgba(78, 122, 245, 1)',
          borderColor: 'rgb(78, 122, 245, 1)',
          borderWidth: 2.5,
          borderRadius: 10,
          pointRadius: 5,
          pointHoverRadius: 7,
          barThickness: 6,
        },
        {
          label: 'Vacant',
          data: vacant,
          backgroundColor: 'rgba(146, 149, 151, 1)',
          borderColor: 'rgb(146, 149, 151, 1)',
          borderWidth: 2.5,
          borderRadius: 10,
          pointRadius: 1,
          pointHoverRadius: 1,
          barThickness: 6,
          pointStyle: 'circle',
        },
      ],
    };
    const getOrCreateTooltip = CommonFunctions.getOrCreateTooltip();

    const externalTooltipHandler = (context) => {
      // Tooltip Element
      const { chart, tooltip } = context;
      const tooltipEl = getOrCreateTooltip(chart);

      // Hide if no tooltip
      if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = 0;
        return;
      }

      // Set Text
      if (tooltip.body) {
        const titleLines = tooltip.title || [];
        const bodyLines = tooltip.body.map((b) => b.lines);
        const tableHead = document.createElement('thead');

        titleLines.forEach((title) => {
          const tr = document.createElement('tr');
          tr.style.borderWidth = '0';

          const th = document.createElement('th');
          th.style.borderWidth = '0';
          const year = data[tooltip.dataPoints[0].dataIndex].year;

          const titleText = title + ', ' + year;
          const text = document.createTextNode(titleText);

          th.appendChild(text);
          tr.appendChild(th);
          tableHead.appendChild(tr);
        });

        const tableBody = document.createElement('tbody');
        bodyLines.forEach((body, i) => {
          const colors = tooltip.labelColors[i];
          const span = document.createElement('span');
          span.style.color = colors.borderColor;

          const tr = document.createElement('tr');
          tr.style.backgroundColor = 'inherit';
          tr.style.borderWidth = '0';

          const td = document.createElement('td');
          td.style.borderWidth = '0';
          td.style.padding = "6px 12px";
          let colonIndex = body[0]?.indexOf(':');
          let extractedString = body[0]?.substring(0, colonIndex);
          const label = extractedString;
          const modifiedBody = body.map((line) => {
            const [beforeColon, afterColon] = line.split(':');
            const extractedString = afterColon.trim();
            return extractedString + "%";
          });

          modifiedBody.forEach((modifiedLine) => {
            const td1 = document.createElement('td');
            td1.style.borderWidth = '0';
            td1.style.padding = "6px 12px";
            td1.style.textAlign = "right";
            const labelNode = document.createTextNode(label);
            span.innerHTML = modifiedLine;
            const svg = document.createElementNS(
              'http://www.w3.org/2000/svg',
              'svg'
            );
            svg.setAttribute('width', '20');
            svg.setAttribute('height', '20');
            td.style.display = "flex";
            td.style.alignItems = "center";
            td.style.gap = "6px";
            svg.style.width = "14px";
            svg.style.height = "14px";
            svg.style.flexShrink = "0";
            span.style.display = "inline-block";
            svg.style.verticalAlign = 'middle'; // Align the SVG to the middle of the text
            span.style.verticalAlign = 'middle'; // Align the text to the middle
            td.appendChild(labelNode);
            tr.appendChild(td);
            td1.appendChild(span);
            tr.appendChild(td1);
            tableBody.appendChild(tr);
          });
        });

        const tableRoot = tooltipEl.querySelector('table');

        while (tableRoot.firstChild) {
          tableRoot.firstChild.remove();
        }
        tableRoot.appendChild(tableHead);
        tableRoot.appendChild(tableBody);
      }

      const positionX = chart.canvas.offsetLeft;
      const positionY = chart.canvas.offsetTop;
      const totalBars = chart.data.labels.length; // Total number of bars
      const tooltipIndex = tooltip.dataPoints[0].dataIndex; // Get hovered bar index

      tooltipEl.style.opacity = 1;

      // Check if the hovered bar is among the last three

      if (tooltipIndex >= totalBars - 3 && totalBars > 3) {
        tooltipEl.style.left = positionX + tooltip.caretX - tooltipEl.offsetWidth - 10 + 'px';
      } else {
        tooltipEl.style.left = positionX + tooltip.caretX + 'px';
      }

      tooltipEl.style.top = positionY + tooltip.caretY + 'px';
      tooltipEl.style.font = tooltip.options.bodyFont.string;
      tooltipEl.style.paddingLeft = tooltip.options.padding + 5 + 'px';
      tooltipEl.style.paddingRight = tooltip.options.padding + 5 + 'px';
      tooltipEl.style.paddingBottom = tooltip.options.padding + 1 + 'px';
      tooltipEl.style.paddingTop = tooltip.options.padding + 1 + 'px';

    };

    this.chartOptions = {
      interaction: {
        mode: 'index',
        intersect: false,
      },
      responsive: true,
      aspectRatio: 1.3,
      maintainAspectRatio: false,
      scales: {
        x: {
          type: 'category',
          grid: {
            display: false,
          },
        },
        y: {
          max : 100,
          beginAtZero: true,
          ticks: {
            stepSize: 10,
            callback: function (value) {
              return value + '%';
            },
          },
        },
      },
      plugins: {
        tooltip: {
          enabled: false,
          position: 'nearest',
          external: externalTooltipHandler,
        },
        legend: {
          position: 'bottom',
          labels: {
            boxHeight: 6,
            boxWidth: 6,
          },
        },
      },
      hover: {
        mode: 'nearest',
        intersect: false,
      },
      layout: {
        padding: {
          left: 20,
          right: 20,
          top: 10,
        },
      },
      elements: {
        line: {
          tension: 0.2,
        },
        point: {
          radius: function (context) {
            return context.parsed.y > 0 ? 5 : 0;
          },
        },
      },
    };
  }

  createRevenueChart(data: any[]) {
    const myCustomPipe = new DigitFormatPipe();
    const labels = data.map((item) => getMonthAbbreviation(item.month));
    const receivedData = [];
    const refundedData = [];
    data.forEach((item) => {
      receivedData.push(item.revenue.received > 0 ? item.revenue.received : 0);
      refundedData.push(item.revenue.refunded > 0 ? item.revenue.refunded : 0);
    });
    this.revenueData = {
      labels: labels,
      datasets: [
        {
          label: 'Received',
          data: receivedData,
          backgroundColor: 'rgba(78, 122, 245, 1)',
          borderColor: 'rgb(78, 122, 245, 1)',
          borderWidth: 1,
          maxBarThickness: 30,
          pointRadius: 6, // Default size
          pointHoverRadius: 6, // Enlarges on hover
          pointBackgroundColor: 'rgb(78, 122, 245, 1)', // Outer circle
          pointBorderColor: '#ffffff', // Inner circle
          pointBorderWidth: 6,
          pointHoverBackgroundColor: 'rgba(78, 122, 245, 1)', // Inner circle changes on hover
          pointHoverBorderColor: '#ffffff', // Outer circle on hover
          pointHoverBorderWidth: 6,
          clip : false
        },
      ],
    };
    const getOrCreateTooltip = CommonFunctions.getOrCreateTooltip();

    const externalTooltipHandler = (context) => {
      const { chart, tooltip } = context;
      const tooltipEl = getOrCreateTooltip(chart);

      if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = 0;
        return;
      }

      if (tooltip.body) {
        const titleLines = tooltip.title || [];
        const bodyLines = tooltip.body.map((b) => b.lines);
        const tableHead = document.createElement('thead');

        titleLines.forEach((title) => {
          const tr = document.createElement('tr');
          tr.style.borderWidth = '0';

          const th = document.createElement('th');
          th.style.borderWidth = '0';
          th.style.textAlign = "center";
          const year = data[tooltip.dataPoints[0].dataIndex].year;

          const titleText = title + ', ' + year;
          const text = document.createTextNode(titleText);

          th.appendChild(text);
          tr.appendChild(th);
          tableHead.appendChild(tr);
        });

        const tableBody = document.createElement('tbody');
        bodyLines.forEach((body, i) => {
          const colors = tooltip.labelColors[i];
          const span = document.createElement('span');
          span.style.color = colors.borderColor;
          span.style.display = "inline-block";
          span.style.minWidth = "60px";
          span.style.textAlign = "right";
          const tr = document.createElement('tr');
          tr.style.backgroundColor = 'inherit';
          tr.style.borderWidth = '0';

          const td = document.createElement('td');
          td.style.borderWidth = '0';
          td.style.padding = "6px 12px";
          td.style.textAlign = "left";
          let colonIndex = body[0]?.indexOf(':');
          let extractedString = body[0]?.substring(0, colonIndex);
          const label = extractedString;
          const modifiedBody = body.map((line) => {
            const [ beforeColon, afterColon] = line.split(':');
            const extractedString = afterColon.trim();
            return '$' + extractedString;
          });
          modifiedBody.forEach((modifiedLine) => {
            const td1 = document.createElement('td');
            td1.style.borderWidth = '0';
            td1.style.padding = "6px 12px";
            td1.style.textAlign = "right";
            const labelNode = document.createTextNode(label);
            span.innerHTML = modifiedLine;
            const svg = document.createElementNS(
              'http://www.w3.org/2000/svg',
              'svg'
            );
            svg.setAttribute('width', '20');
            svg.setAttribute('height', '20');
            td.style.display = "flex";
            td.style.alignItems = "center";
            td.style.gap = "6px";
            svg.style.width = "14px";
            svg.style.height = "14px";
            svg.style.flexShrink = "0";
            span.style.display = "inline-block";
            if (labelNode.data === 'Received') {
              svg.innerHTML =
                '<path d="M12.0991 11.2591L9.72042 12.2388C9.61527 12.2821 9.49723 12.2818 9.39226 12.2381C9.28729 12.1944 9.20399 12.1107 9.16069 12.0056C9.11738 11.9004 9.11762 11.7824 9.16135 11.6774C9.20508 11.5725 9.28872 11.4892 9.39386 11.4459L10.8155 10.8603L6.17329 8.92679C6.06844 8.88295 5.98528 8.79929 5.94209 8.69417L4.70345 5.68668L0.389599 3.88961C0.286587 3.84458 0.205382 3.7609 0.163474 3.65658C0.121567 3.55226 0.122309 3.43565 0.165541 3.33187C0.208774 3.22809 0.291037 3.14544 0.394613 3.10173C0.49819 3.05802 0.614793 3.05673 0.719307 3.09815L5.19716 4.96354C5.30213 5.00725 5.38544 5.09087 5.42876 5.19599L6.66723 8.20309L11.1451 10.0685L10.5597 8.64719C10.5164 8.54205 10.5167 8.424 10.5604 8.31903C10.6041 8.21406 10.6878 8.13077 10.7929 8.08746C10.898 8.04416 11.0161 8.04439 11.1211 8.08812C11.226 8.13185 11.3093 8.21549 11.3526 8.32064L12.3323 10.6994C12.3756 10.8045 12.3754 10.9225 12.3316 11.0275C12.2879 11.1325 12.2043 11.2158 12.0991 11.2591Z" fill="#4E7AF5"/>';
              span.style.color = 'rgba(78, 122, 245, 1)';
            } else {
              svg.innerHTML =
                '<path d="M12 0.42876V3.00132C12 3.11503 11.9548 3.22409 11.8744 3.3045C11.794 3.3849 11.685 3.43008 11.5712 3.43008C11.4575 3.43008 11.3485 3.3849 11.2681 3.3045C11.1877 3.22409 11.1425 3.11503 11.1425 3.00132V1.46379L7.58678 5.01992C7.50632 5.10017 7.39729 5.1452 7.28364 5.14511H4.03107L0.726624 8.44956C0.645759 8.52767 0.537453 8.57088 0.425034 8.56991C0.312615 8.56893 0.205076 8.52384 0.125581 8.44434C0.0460853 8.36485 0.000993102 8.25731 1.62082e-05 8.14489C-0.000960686 8.03247 0.0422558 7.92416 0.120358 7.8433L3.55043 4.41322C3.63082 4.33281 3.73986 4.28762 3.85357 4.2876H7.10571L10.5358 0.857519H8.99868C8.88497 0.857519 8.77591 0.812346 8.6955 0.731938C8.6151 0.65153 8.56992 0.542474 8.56992 0.42876C8.56992 0.315045 8.6151 0.205989 8.6955 0.125581C8.77591 0.0451728 8.88497 0 8.99868 0H11.5712C11.685 0 11.794 0.0451728 11.8744 0.125581C11.9548 0.205989 12 0.315045 12 0.42876Z" fill="#B0B0B0"/>';
              span.style.color = 'rgba(146, 149, 151, 1)';
            }
            svg.style.verticalAlign = 'middle'; // Align the SVG to the middle of the text
            span.style.verticalAlign = 'middle'; // Align the text to the middle
            td.appendChild(svg);
            td.appendChild(labelNode);
            tr.appendChild(td);
            td1.appendChild(span);
            tr.appendChild(td1);
            tableBody.appendChild(tr);
          });
        });

        const tableRoot = tooltipEl.querySelector('table');

        while (tableRoot.firstChild) {
          tableRoot.firstChild.remove();
        }

        tableRoot.appendChild(tableHead);
        tableRoot.appendChild(tableBody);
      }

const positionX = chart.canvas.offsetLeft; // Shift tooltip slightly left
      const positionY = chart.canvas.offsetTop; // Shift tooltip slightly upwards

      const totalBars = chart.data.labels.length; // Total bars in chart
      const tooltipIndex = tooltip.dataPoints[0].dataIndex; // Current hovered bar index

      tooltipEl.style.opacity = 1;

      // Check if the tooltip is for the last 3 bars
      if (tooltipIndex === 0) {
        // Always open the first tooltip to the right
        tooltipEl.style.left = positionX + tooltip.caretX + 'px';
      } else if (tooltipIndex >= totalBars - 3) {
        // Shift tooltip to the left
        tooltipEl.style.left = positionX + tooltip.caretX - tooltipEl.offsetWidth - 10 + 'px';
      } else {
        // Default position
        tooltipEl.style.left = positionX + tooltip.caretX + 'px';
      }

      tooltipEl.style.top = positionY + tooltip.caretY + 'px';
      tooltipEl.style.font = tooltip.options.bodyFont.string;
      tooltipEl.style.paddingLeft = tooltip.options.padding + 5 + 'px';
      tooltipEl.style.paddingRight = tooltip.options.padding + 5 + 'px';
      tooltipEl.style.paddingBottom = tooltip.options.padding + 1 + 'px';
      tooltipEl.style.paddingTop = tooltip.options.padding + 1 + 'px';

    };
    const isSingleMonth = labels.length === 1;
    this.chartOptionsForGraph = CommonFunctions.chartOptionsForGraph(isSingleMonth, externalTooltipHandler, myCustomPipe, 10);
  }

  getDashboardDataByManagerId(req) {
    this.superAdminService.getDashboardDataByManagerId(req)
    .pipe(autoUnsubWith(this.destroyRef))
    .subscribe({
      next: (response: ResponseApiResponse) => {
        this.totalBookings = response.getBookings.total;
        this.revenueWithTransactionFees = response.getBookings.revenueWithTransactionFees;
        this.revenueWithTaxAndFees = response.getBookings.revenueWithTaxAndFees;
        this.totalRevenue = response.getRevenue.total;
        this.peakHoursStartTime = response.getBookings.peakHoursStartTime;
        this.peakHoursEndTime = response.getBookings.peakHoursEndTime;
        this.groupBookingCount = response.getBookings.groupBookingCount;
        this.groupBookingSlotCounts = response.getBookings.groupBookingSlotCounts;
        this.oneTimeBookingCount = response.getBookings.oneTimeBookingCount;
        this.parkerCount = response.getBookings.driverBookingCount;
        if (this.peakHoursStartTime && this.peakHoursEndTime) {
          this.peakHoursStartTime = this.convertToTimeFormat(
            this.peakHoursStartTime
          );
          this.peakHoursEndTime = this.convertToTimeFormat(
            this.peakHoursEndTime
          );
        }

      },
      error: () => {
        this.totalBookings = 0;
        this.totalRevenue = 0;
        this.peakHoursStartTime = '';
        this.peakHoursEndTime = '';

      },
    });
  }

  showChart() {
    this.parkingStatsData = {
      datasets: [
        {
          data: [
            (this.occupiedCount || 0).toString(),
            (this.maintenanceCount || 0).toString(),
            (this.vacantCount || 0).toString(),
          ],
          backgroundColor: ['#017EFA', '#FF3D56', '#5FD3B7'],
        },
      ],
    };

    this.parkingStatsOptions = {
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      responsive: false,
      maintainAspectRatio: false,
    };
  }

  convertToTimeFormat(time: string): string {
    let formattedTime = time.length <= 1 ? '0' + time : time.toString();

    formattedTime += ':00';
    return formattedTime;
  }

  getOccupiedAndVacantParkingLots(req) {
    this.superAdminService.getOccupiedAndVacantParkingLots(req)
    .pipe(autoUnsubWith(this.destroyRef))
    .subscribe({
      next: (response: any) => {
        this.vacantCount = response.vacant;
        this.occupiedCount = response.occupancyRate;
        this.maintenanceCount = response.underMaintenance;
        this.showChart();

      },
    });
  }

  onOptionSelected(event: any) {
    this.selectedParkingLot = event.event.value === '1' ? null : event.event.value;
    if (!event.reset) {
      this.searchByParkingLot();
    }
  }

  generateYears(): void {
    const currentYear = new Date().getFullYear(); // Get current year dynamically
    const startYear = 2024;
    this.year = Array.from(
      { length: currentYear - startYear + 1 },
      (_, index) => ({ name: (startYear + index).toString() })
    );
  }

  dateRangeDataFuc(event){
    this.dateRangeData = event;
  }
}
